import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  iDataNivel,
  NivelesFields,
} from "../../../interfaces/SuperAdmin/Pages/iDataNivel";
import {
  deleteNiveles,
  getNivelesByOrgId,
  postNivel,
  putNivelById,
} from "../../../api/broker/nivel";
import { toast } from "react-toastify";

interface NivelesContextProps {
  niveles: iDataNivel[];

  toEditNivel: iDataNivel | null;
  selectedNiveles: string[];
  setSelectedNiveles: Dispatch<SetStateAction<string[]>>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;

  isEditModalOpen: boolean;
  isCreateModalOpen: boolean;
  isDeleteModalOpen: boolean;

  isLoading: boolean;
  isEditing: boolean;
  isCreating: boolean;
  isDeleting: boolean;

  handleEditNivel: (nivel: iDataNivel) => void;
  toggleEditModal: (isOpen: boolean) => void;
  toggleCreateModal: (isOpen: boolean) => void;
  toggleDeleteModal: (isOpen: boolean) => void;

  nivelEdit: (data: NivelesFields, id_nivel: string) => Promise<void>;
  nivelCreate: (data: NivelesFields, id_organizacion: string) => Promise<void>;
  nivelDelete: (body: { ids: string[] }) => Promise<void>;
}

const NivelesContext = createContext<NivelesContextProps>(
  {} as NivelesContextProps
);

interface Props {
  children: React.ReactNode;
}

const NivelesProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();

  const [niveles, setNiveles] = useState<iDataNivel[]>([]);

  const [toEditNivel, setToEditNivel] = useState<iDataNivel | null>(null);
  const [selectedNiveles, setSelectedNiveles] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Función para extraer niveles
  const fetchNiveles = async (id: string) => {
    setIsLoading(true);
    try {
      const nivelesRes = await getNivelesByOrgId(id);
      console.log(nivelesRes);

      if (nivelesRes.data.statusCode === 200) {
        setNiveles(nivelesRes.data.body);
      } else {
        toast.error("Error al obtener los niveles de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los niveles de la organización");
    } finally {
      setIsLoading(false);
    }
  };

  // Crear nivel
  const nivelCreate = async (nivel: NivelesFields, id_organizacion: string) => {
    setIsCreating(true);
    try {
      //Creamos el nivel
      const nivelRes = await postNivel(nivel, id_organizacion);
      console.log(nivelRes);

      if (nivelRes.data.statusCode === 201) {
        setNiveles((prev) => [...prev, nivelRes.data.body]);
        toggleCreateModal(false);
        toast.success("Nivel creado exitosamente");
      } else {
        toast.error("Error al crear nivel en la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al crear nivel en la organización");
    } finally {
      setIsCreating(false);
    }
  };

  // Edicion de nivel
  const nivelEdit = async (data: NivelesFields, id_nivel: string) => {
    setIsEditing(true);
    try {
      const res = await putNivelById(data, id_nivel);
      console.log(res);
      if (res.data.statusCode === 200) {
        setNiveles((prev) =>
          prev.map((nivel) => {
            if (nivel.id_nivel === id_nivel) {
              return res.data.body;
            }
            return nivel;
          })
        );
        toggleEditModal(false);
        toast.success("Nivel editado exitosamente");
      } else {
        toast.error("Error al editar nivel");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar nivel");
    } finally {
      setIsEditing(false);
    }
  };

  // Eliminar niveles
  const nivelDelete = async (body: { ids: string[] }) => {
    setIsDeleting(true);
    try {
      const res = await deleteNiveles(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setSelectedNiveles([]);
        setNiveles((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_nivel))
        );
        toggleDeleteModal(false);
        toast.success("Niveles eliminados exitosamente");
      } else {
        toast.error("Error al eliminar los niveles de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar los niveles de la organización");
    } finally {
      setIsDeleting(false);
    }
  };

  // Abrir menu de editar nivel
  const toggleEditModal = (isOpen: boolean) => {
    if (!isOpen) {
      setToEditNivel(null);
    }
    setIsEditModalOpen(isOpen);
  };

  // Abrir menu de crear nivel
  const toggleCreateModal = (isOpen: boolean) => {
    setIsCreateModalOpen(isOpen);
  };

  // Abrir menu de eliminar niveles
  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // Seleccionar nivel a editar
  const handleEditNivel = (nivel: iDataNivel) => {
    setToEditNivel(nivel);
    toggleEditModal(true);
  };

  useEffect(() => {
    if (idOrganization) {
      fetchNiveles(idOrganization);
    }
  }, [idOrganization]);

  return (
    <NivelesContext.Provider
      value={{
        niveles,
        setSelectedNiveles,
        toEditNivel,
        selectedNiveles,
        searchText,
        setSearchText,
        isEditModalOpen,
        isCreateModalOpen,
        isDeleteModalOpen,
        isLoading,
        isEditing,
        isCreating,
        isDeleting,
        handleEditNivel,
        toggleEditModal,
        toggleCreateModal,
        toggleDeleteModal,
        nivelEdit,
        nivelCreate,
        nivelDelete,
      }}
    >
      {children}
    </NivelesContext.Provider>
  );
};

const useNiveles = () => useContext(NivelesContext);

export { NivelesProvider, useNiveles };
